import React, { useState } from 'react';
import { 
  Form, 
  Input, 
  InputNumber, 
  Select, 
  Switch, 
  Button, 
  Card, 
  Row, 
  Col, 
  Divider,
  message,
  Space
} from 'antd';
import { 
  ArrowLeftOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import planService from '../../services/planService';
import { routes } from '../../utils/routes.config';
import { Typography } from 'antd';

const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

// Currency options
const CURRENCIES = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
];

const CreatePlan = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('INR');

  const handleCurrencyChange = (currency: string) => {
    setSelectedCurrency(currency);
    const currencyData = CURRENCIES.find(c => c.code === currency);
    form.setFieldsValue({
      currency,
      currencySymbol: currencyData?.symbol || '₹',
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      const res = await planService.createPlan(values);
      if (res?.success !== false) {
        message.success('Plan created successfully');
        navigate(routes.PLANS);
      } else {
        message.error(res?.message || 'Failed to create plan');
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || error.message || 'Failed to create plan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <Card
        title={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(routes.PLANS)}
            >
              Back
            </Button>
            <Title level={4} style={{ margin: 0 }}>Create New Plan</Title>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            planType: 'subscription',
            currency: 'INR',
            currencySymbol: '₹',
            billingPeriod: 'monthly',
            maxWarehouses: 1,
            maxDrivers: 5,
            isActive: true,
            isPopular: false,
            sortOrder: 0,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Plan Name"
                rules={[{ required: true, message: 'Please enter plan name' }]}
              >
                <Input placeholder="e.g., Basic Plan" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="planType"
                label="Plan Type"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="subscription">Subscription</Option>
                  <Option value="one_time">One-Time</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="billingPeriod"
                label="Billing Period"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="monthly">Monthly</Option>
                  <Option value="yearly">Yearly</Option>
                  <Option value="lifetime">Lifetime</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Plan description..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="currency"
                label="Currency"
                rules={[{ required: true }]}
              >
                <Select onChange={handleCurrencyChange}>
                  {CURRENCIES.map(curr => (
                    <Option key={curr.code} value={curr.code}>
                      {curr.symbol} {curr.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="price"
                label="Price"
                rules={[{ required: true, message: 'Please enter price' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="currencySymbol"
                label="Currency Symbol"
                rules={[{ required: true }]}
              >
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Plan Limits</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="maxWarehouses"
                label="Max Warehouses"
                rules={[{ required: true }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="maxDrivers"
                label="Max Drivers"
                rules={[{ required: true }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="maxOrders"
                label="Max Orders/Month"
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Unlimited" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="sortOrder"
                label="Sort Order"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="isActive"
                valuePropName="checked"
                label="Active"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="isPopular"
                valuePropName="checked"
                label="Popular Plan"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginTop: 24 }}>
            <Button 
              type="primary" 
              htmlType="submit" 
              block 
              size="large"
              icon={<SaveOutlined />}
              loading={loading}
            >
              Create Plan
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </MainLayout>
  );
};

export default CreatePlan;

